import { IMAGE_FLOATS } from '@/utils/richText/imageFloats';
import { imageFloatClass } from '@/utils/richText/imageFloatClass';
import { encodeImageLayoutInSrc } from '@/utils/html/encodeImageLayoutInSrc';
import { decodeImageLayoutFromSrc } from '@/utils/html/decodeImageLayoutFromSrc';

export function decodeHtml(html: string): string {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

export function replaceImagesWithMarkdown(html: string): string {
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    const src = tag.match(/\bsrc="([^"]+)"/i)?.[1];

    if (!src) {
      return tag;
    }

    const width = tag.match(/\bwidth="([^"]+)"/i)?.[1];
    const classNames = (tag.match(/\bclass="([^"]*)"/i)?.[1] ?? '').split(/\s+/);
    const float = IMAGE_FLOATS.find((value) => classNames.includes(imageFloatClass(value)));
    const filename = src.split('#')[0].split('/').pop() || 'image.png';
    return `![${filename}](${encodeImageLayoutInSrc(src, { width, float })})`;
  });
}

export function extractTextWithImages(input: string): string {
  // Decode HTML entities
  const decodedDoc = new DOMParser().parseFromString(input, 'text/html');
  const decodedHTML = decodedDoc.body.innerHTML;

  // Parse the decoded HTML
  const doc = new DOMParser().parseFromString(decodedHTML, 'text/html');

  function traverse(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;

      if (el.tagName.toLowerCase() === 'img') {
        return el.outerHTML; // keep <img> as-is
      }

      // Preserve user mention links
      if (el.tagName.toLowerCase() === 'a' && el.classList.contains('ql-mention')) {
        return el.outerHTML;
      }

      let result = '';

      // Preserve line breaks for <p>, <br>, etc.
      const blockTags = ['p', 'div', 'br'];
      const tag = el.tagName.toLowerCase();

      if (blockTags.includes(tag)) result += '\n';

      for (const child of Array.from(el.childNodes)) {
        result += traverse(child);
      }

      if (blockTags.includes(tag)) result += '\n';

      return result;
    }

    return '';
  }

  const output = traverse(doc.body);

  // Normalize whitespace: trim and collapse excessive empty lines
  return output.replace(/\n{3,}/g, '\n\n').trim();
}

export function convertMarkdownImagesToHtml(html: string): string {
  // Convert markdown images ![alt](url) to <img> tags
  return html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt, encodedSrc) => {
    const { src, width, float } = decodeImageLayoutFromSrc(encodedSrc);
    const widthAttr = width ? ` width="${width}"` : '';
    const classAttr = float ? ` class="${imageFloatClass(float)}"` : '';
    return `<img src="${src}" alt="${alt}"${widthAttr}${classAttr} />`;
  });
}
